import { Storage } from '../lib/storage';
import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ShoppingBag, Search, Plus, User, RefreshCw, 
  MessageCircle, Flag, ShieldCheck, Crown, 
  ChevronRight, ChevronLeft, Camera, Trash2, CheckCircle2,
  AlertTriangle, X, MapPin, Bike, Share2
} from 'lucide-react';
import { supabase } from '../lib/supabase';

export default function MotomarketScreen({ onOpenExpediente, idAutoOpen, onAdOpened }: { onOpenExpediente?: () => void, idAutoOpen?: string | null, onAdOpened?: () => void }) {
  const [anuncios, setAnuncios] = useState<any[]>([]);
  const [busqueda, setBusqueda] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadingMas, setLoadingMas] = useState(false);
  const [pagina, setPagina] = useState(0);
  const [hayMas, setHayMas] = useState(true);
  const BATCH_SIZE = 10;
  const [esOro, setEsOro] = useState(false);
  const [userId, setUserId] = useState<string | null>(null);
  const [soyEmpleado, setSoyEmpleado] = useState(false);

  // Estados para el Visor de Fotos
  const [visorFotos, setVisorFotos] = useState<{ fotos: string[], index: number, ad: any } | null>(null);

  // Estados para Publicar
  const [mostrarModalPublicar, setMostrarModalPublicar] = useState(false);
  const [tituloAnuncio, setTituloAnuncio] = useState('');
  const [precio, setPrecio] = useState('');
  const [descripcion, setDescripcion] = useState('');
  const [publicando, setPublicando] = useState(false);
  const [comprimiendo, setComprimiendo] = useState(false);

  // Estados para Mi Perfil de Ventas
  const [viendoMisAnuncios, setViendoMisAnuncios] = useState(false);
  const [anuncioEnEdicion, setAnuncioEnEdicion] = useState<any>(null);
  

  const [ubicacion, setUbicacion] = useState('');
  const [fotosExtra, setFotosExtra] = useState<string[]>([]);

  // Sistema de Alertas Local
  const [dialogoConfirmacion, setDialogoConfirmacion] = useState<{
    titulo: string, 
    mensaje: string, 
    color: 'amber' | 'emerald' | 'red',
    accion?: () => void
  } | null>(null);

  useEffect(() => {
    inicializar();
  }, []);

  useEffect(() => {
    if (idAutoOpen) {
      abrirAdPorId(idAutoOpen);
    }
  }, [idAutoOpen]);

  const abrirAdPorId = async (id: string) => {
    try {
      const { data } = await supabase
        .from('t_concesionario')
        .select(`
          *,
          vehiculos (
            *,
            documentos_legales (*)
          ),
          usuarios:usuario_id (
            id,
            nombre,
            celular
          )
        `)
        .eq('id', id)
        .single();
      
      if (data) {
        setVisorFotos({ ad: data, index: 0, fotos: data.fotos || [data.vehiculos?.foto_url] });
        onAdOpened?.();
      }
    } catch (e) {
      console.error("Error cargando ad compartido:", e);
    }
  };

  const inicializar = async () => {
    const uid = Storage.get('gravity_user_id');
    const userJson = Storage.get('gravity_user');
    setUserId(uid);
    if (userJson) {
      const parsed = JSON.parse(userJson);
      setEsOro(!!parsed.es_oro);
      setSoyEmpleado(parsed.rol === 'empleado_taller');
    }
    fetchAnuncios();
  };

  const procesarImagen = (file: File): Promise<Blob> => {
    return new Promise((resolve, reject) => {
      try {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onerror = reject;
        reader.onload = (e) => {
          const img = new Image();
          img.src = e.target?.result as string;
          img.onerror = reject;
          img.onload = () => {
            try {
              const canvas = document.createElement('canvas');
              
              // NORMALIZACIÓN 4:3 (Landscape Profesional)
              const targetWidth = 1024;
              const targetHeight = 768; 
              
              canvas.width = targetWidth;
              canvas.height = targetHeight;
              const ctx = canvas.getContext('2d');

              if (!ctx) {
                reject(new Error("No se pudo obtener el contexto del canvas"));
                return;
              }

              // Lógica de "Object-Cover" para el Canvas
              const imgRatio = img.width / img.height;
              const targetRatio = targetWidth / targetHeight;

              let sx, sy, sWidth, sHeight;
              if (imgRatio > targetRatio) {
                // La imagen es más ancha que 4:3 -> Cortamos laterales
                sHeight = img.height;
                sWidth = img.height * targetRatio;
                sx = (img.width - sWidth) / 2;
                sy = 0;
              } else {
                // La imagen es más alta que 4:3 -> Cortamos arriba/abajo
                sWidth = img.width;
                sHeight = img.width / targetRatio;
                sx = 0;
                sy = (img.height - sHeight) / 2;
              }

              // Dibujar la imagen recortada y escalada al lienzo 4:3
              ctx.drawImage(img, sx, sy, sWidth, sHeight, 0, 0, targetWidth, targetHeight);

              // Exportar a WebP con compresión optimizada
              canvas.toBlob((blob) => {
                if (blob) {
                  // Si el archivo es muy pesado, re-comprimir con menos calidad
                  if (blob.size > 120 * 1024) {
                     canvas.toBlob((b2) => {
                       if (b2) resolve(b2);
                       else resolve(blob);
                     }, 'image/webp', 0.5);
                  } else {
                     resolve(blob);
                   }
                } else {
                  reject(new Error("Canvas toBlob failed"));
                }
              }, 'image/webp', 0.7);
            } catch (err) { reject(err); }
          };
        };
      } catch (err) { reject(err); }
    });
  };

  const handleCompartir = async (ad: any) => {
    const nombre = (ad.vehiculos?.apodo || 'Vehículo VIP').trim();
    const precio = (ad.precio || 0).toLocaleString();
    const ciudad = (ad.ciudad || 'Colombia').trim();
    const url = `https://pikishub.com/gravitybike/?mode=market&ad=${ad.id}`; // Link Inteligente Directo

    const texto = `💎 ¡MIRA ESTA JOYA EN GRAVITY BIKE! 🚲\n\n📌 *Vehículo:* ${nombre.toUpperCase()}\n💰 *Precio:* $${precio}\n📍 *Ubicación:* ${ciudad}\n✅ *Mercado Ciclista VIP*\n\n👇 Ver más fotos y contactar al vendedor aquí:\n${url}`;

    if (navigator.share) {
      try {
        await navigator.share({
          title: `Gravity Bike - ${nombre}`,
          text: texto,
          url: url
        });
      } catch (err) {
        console.log('Error compartiendo:', err);
      }
    } else {
      // Fallback: Copiar al portapapeles
      try {
        await navigator.clipboard.writeText(texto);
        setDialogoConfirmacion({
          titulo: "¡LISTO PARA PEGAR!",
          mensaje: "El enlace y los datos del artículo han sido copiados. Ya puedes pegarlos en WhatsApp o Redes Sociales.",
          color: 'emerald'
        });
      } catch (err) {
        // PLAN C: Ante falta de permisos (HTTP), abrimos WhatsApp directamente
        const wsUrl = `https://api.whatsapp.com/send?text=${encodeURIComponent(texto)}`;
        window.open(wsUrl, '_blank');
      }
    }
  };

  const handleSubirFoto = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    if (fotosExtra.length + files.length > 3) {
      alert("Máximo 3 fotos por anuncio.");
      return;
    }

    setComprimiendo(true);
    const nuevasFotos: string[] = [];

    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const blob = await procesarImagen(file);
        
        const pathUserId = userId || Storage.get('gravity_user_id') || 'anon';
        const fileName = `${pathUserId}/${Date.now()}-${i}.webp`;
        
        let finalUrl = '';
        try {
          const { error } = await supabase.storage
            .from('motos')
            .upload(fileName, blob, { contentType: 'image/webp' });

          if (error) throw error;
          
          const { data } = supabase.storage
            .from('motos')
            .getPublicUrl(fileName);
          
          if (data?.publicUrl) {
            finalUrl = data.publicUrl;
          }
        } catch (uploadErr) {
          console.warn("Fallo de subida a Storage del Mercado. Aplicando auto-curación con fallback de Base64...", uploadErr);
          // Fallback auto-curativo: Convertir el blob WebP optimizado en Base64
          finalUrl = await new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(blob);
            reader.onloadend = () => resolve(reader.result as string);
            reader.onerror = reject;
          });
        }
        
        if (finalUrl) {
          nuevasFotos.push(finalUrl);
        }
      }
      setFotosExtra(prev => [...prev, ...nuevasFotos]);
    } catch (err) {
      console.error("Error subida:", err);
      alert("Error al procesar o subir la foto. Verifica conexión o permisos de almacenamiento.");
    } finally {
      setComprimiendo(false);
      // Reset input para permitir subir la misma foto si se borra
      e.target.value = '';
    }
  };

  const eliminarFoto = (url: string) => {
    setFotosExtra(prev => prev.filter(f => f !== url));
  };

  const fetchAnuncios = async (reset = true, filterMine = false, searchStr = '') => {
    if (reset) {
       setLoading(true);
       setPagina(0);
       setHayMas(true);
    } else {
       setLoadingMas(true);
    }

    try {
      const currentPage = reset ? 0 : pagina + 1;
      const from = currentPage * BATCH_SIZE;
      const to = (currentPage + 1) * BATCH_SIZE - 1;

      let query = supabase
        .from('t_concesionario')
        .select(`
          *,
          vehiculos!inner (
            *,
            documentos_legales (*)
          ),
          usuarios:usuario_id (
            id,
            nombre,
            celular
          )
        `)
        .neq('estado', 'ELIMINADO')
        .order('created_at', { ascending: false })
        .range(from, to);

      if (filterMine) {
        query = query.eq('usuario_id', userId);
      } else if (searchStr) {
        const s = `%${searchStr}%`;
        // Búsqueda combinada: ciudad o apodo/placa en la tabla relacionada
        query = query.or(`ciudad.ilike.${s}, vehiculos.apodo.ilike.${s}, vehiculos.placa.ilike.${s}`);
      }

      const { data, error } = await query;

      if (error) throw error;
      
      const nuevosAnuncios = data || [];
      if (reset) {
        setAnuncios(nuevosAnuncios);
      } else {
        setAnuncios(prev => [...prev, ...nuevosAnuncios]);
        setPagina(currentPage);
      }

      if (nuevosAnuncios.length < BATCH_SIZE) {
        setHayMas(false);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
      setLoadingMas(false);
    }
  };

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    if (loading || loadingMas || !hayMas) return;
    
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - scrollTop <= clientHeight + 100) {
      fetchAnuncios(false, viendoMisAnuncios, busqueda);
    }
  };

  

  const handleAbrirPublicar = () => {
    if (!esOro) {
      setDialogoConfirmacion({
        titulo: "ACCESO RESTRINGIDO",
        mensaje: "⚠️ ¡ALTO MOTERO! El Mercado VIP es exclusivo para Miembros ORO. Solo los moteros certificados por la Mesa Redonda pueden publicar y gestionar anuncios con BikeMarket VIP.",
        color: 'amber'
      });
      return;
    }
    setMostrarModalPublicar(true);
  };

  const publicarAnuncio = async () => {
    if (!tituloAnuncio || !precio) return;
    if (fotosExtra.length === 0) {
      alert("Debes subir al menos 1 foto de tu bicicleta.");
      return;
    }

    setPublicando(true);
    try {
      let vId = anuncioEnEdicion?.vehiculo_id;
      
      // Si es un anuncio nuevo, creamos un vehículo fantasma (bici) en la DB
      if (!anuncioEnEdicion) {
         const { data: newV, error: errV } = await supabase.from('vehiculos').insert([{
             usuario_id: userId,
             apodo: tituloAnuncio,
             placa: 'BICI-' + Math.floor(Math.random() * 10000),
             cilindraje: 0
         }]).select('id').single();
         if (errV) throw errV;
         vId = newV.id;
      } else {
         // Actualizar título del vehículo fantasma
         await supabase.from('vehiculos').update({ apodo: tituloAnuncio }).eq('id', vId);
      }

      const payload = {
        usuario_id: userId,
        vehiculo_id: vId,
        precio: parseFloat(precio.replace(/\D/g, '')),
        descripcion,
        estado: 'ACTIVO',
        ciudad: ubicacion,
        fotos: fotosExtra
      };

      if (anuncioEnEdicion) {
         const { error } = await supabase
           .from('t_concesionario')
           .update(payload)
           .eq('id', anuncioEnEdicion.id);
         if (error) throw error;
      } else {
         const { error } = await supabase.from('t_concesionario').insert([payload]);
         if (error) throw error;
      }
      
      setMostrarModalPublicar(false);
      setAnuncioEnEdicion(null);
      setTituloAnuncio('');
      setPrecio('');
      setDescripcion('');
      setFotosExtra([]);
      fetchAnuncios();
      
      setDialogoConfirmacion({
        titulo: anuncioEnEdicion ? "ANUNCIO ACTUALIZADO" : "¡EN VIVO!",
        mensaje: anuncioEnEdicion ? "Los cambios se han guardado correctamente." : "Tu artículo ya está en el Mercado VIP. Los compradores empezarán a contactarte por WhatsApp.",
        color: 'emerald'
      });
    } catch (e) {
      console.error(e);
      alert("Error al guardar publicación.");
    } finally {
      setPublicando(false);
    }
  };

  const handleEditarAnuncio = (ad: any) => {
    setAnuncioEnEdicion(ad);
    setTituloAnuncio(ad.vehiculos?.apodo || '');
    setPrecio(ad.precio.toLocaleString());
    setDescripcion(ad.descripcion || '');
    setUbicacion(ad.ciudad || '');
    setFotosExtra(ad.fotos || []);
    setMostrarModalPublicar(true);
  };

  const republicarAnuncio = async (id: string) => {
    try {
      const { error } = await supabase
        .from('t_concesionario')
        .update({ 
          estado: 'ACTIVO',
          created_at: new Date().toISOString() // Actualiza fecha para subir de primera
        })
        .eq('id', id);
      
      if (error) throw error;
      fetchAnuncios();
      setDialogoConfirmacion({
        titulo: "¡REPUBLICADA!",
        mensaje: "Tu artículo vuelve a estar activo y de primera en el mercado.",
        color: 'emerald'
      });
    } catch (e) {
      console.error(e);
    }
  };

  const reportarAnuncio = async (id: string) => {
    setDialogoConfirmacion({
      titulo: "¿REPORTAR ANUNCIO?",
      mensaje: "¿Has detectado actividad sospechosa o ilegal en esta publicación? El Equipo Pikis auditará el reporte.",
      color: 'red',
      accion: async () => {
        await supabase.from('t_reportes_mercado').insert([{
          publicacion_id: id,
          reportante_id: userId,
          motivo: 'Reporte General desde App',
          estado: 'PENDIENTE'
        }]);
        setDialogoConfirmacion({
          titulo: "REPORTE RECIBIDO",
          mensaje: "Gracias por cuidar la comunidad. Investigaremos de inmediato.",
          color: 'emerald'
        });
      }
    });
  };

  const marcarComoVendido = async (id: string) => {
    const { error } = await supabase
      .from('t_concesionario')
      .update({ estado: 'VENDIDO' })
      .eq('id', id);
    
    if (!error) {
      fetchAnuncios();
      setDialogoConfirmacion({
        titulo: "¡FELICITACIONES!",
        mensaje: "Anuncio marcado como VENDIDO. Una misión exitosa más en Gravity Bike.",
        color: 'emerald'
      });
    }
  };

  const filtrarAnuncios = () => {
    // Ahora el filtrado es principalmente por servidor, 
    // mantenemos esto solo para asegurar estados limpios en la UI
    return anuncios.filter(a => a.estado !== 'ELIMINADO');
  };

  return (
    <div className="flex flex-col h-full bg-transparent overflow-hidden">
      {/* HEADER */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex flex-col">
          <h1 className="text-lg font-black text-white uppercase tracking-tighter">
            {viendoMisAnuncios ? 'Mis Anuncios' : 'Mercado VIP'}
          </h1>
          <span className="text-[8px] text-primary-cyan font-black uppercase tracking-[3px]">
            BikeMarket VIP
          </span>
        </div>
        
        <div className="flex items-center gap-1.5">
          {/* BOTÓN: REFRESCAR */}
          <button 
            onClick={fetchAnuncios}
            className="w-9 h-9 bg-white/5 border border-white/5 rounded-xl flex items-center justify-center text-slate-500 hover:text-white transition-all active:scale-90"
            title="Refrescar"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin text-primary-cyan' : ''}`} />
          </button>

          {/* BOTÓN: MERCADO GENERAL */}
          <button 
            onClick={() => {
              setViendoMisAnuncios(false);
              setBusqueda('');
              fetchAnuncios(true, false, '');
            }}
            className={`w-9 h-9 rounded-xl flex items-center justify-center transition-all ${!viendoMisAnuncios ? 'bg-primary-cyan/10 border border-primary-cyan/30 text-primary-cyan' : 'bg-white/5 border border-white/5 text-slate-500 hover:text-white'}`}
            title="Mercado"
          >
            <ShoppingBag className="w-5 h-5" />
          </button>

          {/* BOTÓN: MIS ANUNCIOS */}
          {!soyEmpleado && (
            <button 
              onClick={() => {
                if (!esOro) {
                  setDialogoConfirmacion({
                    titulo: "ACCESO RESTRINGIDO",
                    mensaje: "⚠️ ¡ALTO MOTERO! La gestión de 'Mis Anuncios' es una función exclusiva para Miembros ORO. Certifícate hoy mismo para vender tus naves.",
                    color: 'amber'
                  });
                  return;
                }
                setViendoMisAnuncios(true);
                setBusqueda('');
                fetchAnuncios(true, true, '');
              }}
              className={`w-9 h-9 rounded-xl flex items-center justify-center transition-all ${viendoMisAnuncios ? 'bg-primary-cyan/10 border border-primary-cyan/30 text-primary-cyan' : 'bg-white/5 border border-white/5 text-slate-500 hover:text-white'}`}
              title="Mis Anuncios"
            >
              <User className="w-5 h-5" />
            </button>
          )}

          {/* BOTÓN: PUBLICAR */}
          {!soyEmpleado && (
            <button 
              onClick={handleAbrirPublicar}
              className="w-9 h-9 bg-primary-cyan text-bg-dark rounded-xl flex items-center justify-center shadow-[0_5px_15px_rgba(0,168,255,0.3)] active:scale-95 transition-transform ml-1"
              title="Publicar"
            >
              <Plus className="w-5 h-5" />
            </button>
          )}
        </div>
      </div>

      {/* BUSCADOR */}
      <div className="relative mb-5 mt-4">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
        <input 
          type="text" 
          placeholder="Buscar modelo o ciudad (Ej. NS200 Cali)..."
          value={busqueda}
          onChange={e => {
            const val = e.target.value;
            setBusqueda(val);
            // Búsqueda inteligente: Dispara al escribir (debounce manual de hecho por el usuario al parar)
            if (val.length > 2 || val.length === 0) {
              fetchAnuncios(true, viendoMisAnuncios, val);
            }
          }}
          className="w-full bg-slate-800/40 border border-white/10 rounded-2xl py-2.5 pl-11 pr-4 text-xs font-bold text-white outline-none focus:border-primary-cyan transition-colors placeholder:text-slate-600 uppercase"
        />
      </div>

      {/* LISTADO */}
      <div 
        onScroll={handleScroll}
        className="flex-1 overflow-y-auto pr-1 pb-24 scrollbar-hide space-y-6"
      >
        {loading && anuncios.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 opacity-40">
            <RefreshCw className="w-8 h-8 animate-spin mb-4" />
            <p className="text-[10px] uppercase font-black tracking-widest">Escaneando Mercado...</p>
          </div>
        ) : filtrarAnuncios().length === 0 ? (
          <div className="text-center py-20 bg-white/5 rounded-3xl border border-dashed border-white/10">
            <ShoppingBag className="w-12 h-12 text-white/10 mx-auto mb-4" />
            <p className="text-slate-500 text-xs font-bold uppercase">No hay artículos que coincidan</p>
          </div>
        ) : filtrarAnuncios().map((ad) => (
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            key={ad.id}
            className="bg-slate-700/60 backdrop-blur-md border border-white/10 rounded-[30px] overflow-hidden shadow-[0_15px_40px_rgba(0,0,0,0.4)] group"
          >
            {/* CONTENIDO DENTRO DEL EFECTO FANTASMA (MÁS SUAVE) */}
            <div className={`transition-all duration-500 ${ad.estado === 'VENDIDO' ? 'opacity-50 grayscale' : ''}`}>
              {/* IMAGEN Y BADGES */}
              <div 
                onClick={() => setVisorFotos({ ad, index: 0, fotos: ad.fotos || [ad.vehiculos?.foto_url] })}
                className="relative h-48 bg-slate-900 overflow-hidden cursor-pointer"
              >
                <img 
                  src={ad.fotos?.[0] || ad.vehiculos?.foto_url} 
                  alt="Artículo" 
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
                <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-black/80 to-transparent pointer-events-none" />
                
                <div className="absolute top-2 left-3 flex flex-row items-center gap-2">
                  {ad.estado === 'VENDIDO' && (
                    <div className="bg-amber-600 text-black text-[8px] font-black px-2 py-0.5 rounded-full flex items-center gap-1 shadow-lg">
                      <CheckCircle2 className="w-3 h-3" /> ESTATUS: VENDIDO
                    </div>
                  )}
                </div>

              </div>
              {/* BLOQUE DE DATOS AGRUPADOS (Abajo) */}
              <div className="px-4 py-3 bg-black/40 flex items-center justify-between gap-2">
                {/* Precio a la Izquierda */}
                <div className="flex items-center gap-0.5 shrink-0">
                  <span className="text-[10px] font-black text-white/30">$</span>
                  <span className="text-base font-black text-white tracking-tighter">
                    {ad.precio?.toLocaleString()}
                  </span>
                </div>

                {/* Info Técnica a la Derecha */}
                <div className="flex flex-col items-end text-right min-w-0">
                  <h3 className="text-[10px] font-black text-white uppercase truncate w-full">
                    {ad.vehiculos?.apodo || 'Artículo Sin Nombre'}
                  </h3>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="text-[8px] text-slate-400 font-bold uppercase truncate flex items-center gap-0.5">
                      <MapPin className="w-2.5 h-2.5 text-primary-cyan/50" /> {ad.ciudad || 'Colombia'}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* ACCIONES (FUERA DEL EFECTO FANTASMA) */}
            <div className="p-3">
              {viendoMisAnuncios ? (
                <div className="flex items-center gap-2">
                  <button 
                    onClick={() => handleEditarAnuncio(ad)}
                    className="flex-1 flex items-center justify-center py-2.5 bg-white/10 border border-white/10 rounded-xl text-white font-black text-[9px] uppercase active:scale-95 transition-transform"
                    title="Editar"
                  >
                    <RefreshCw className="w-3.5 h-3.5 mr-1" /> EDITAR
                  </button>
                  
                  <button 
                    onClick={() => {
                      setDialogoConfirmacion({
                        titulo: "¿ELIMINAR MI PUBLICACIÓN?",
                        mensaje: "Esta acción no se puede deshacer. Se borrará permanentemente del mercado.",
                        color: 'red',
                        accion: async () => {
                          // Acción optimista e inmediata
                          setAnuncios(prev => prev.filter(p => p.id !== ad.id));
                          setDialogoConfirmacion(null);

                          try {
                            // Limpieza de dependencias y borrado definitivo
                            await supabase.from('t_reportes_mercado').delete().eq('publicacion_id', ad.id);
                            const { error } = await supabase.from('t_concesionario').delete().eq('id', ad.id);
                            if (error) throw error;
                          } catch (error: any) {
                            console.error('Delete Error:', error);
                            // Solo restauramos la lista si hubo un error real
                            fetchAnuncios();
                          }
                        }
                      });
                    }}
                    className="flex-1 flex items-center justify-center py-2.5 bg-red-600/10 border border-red-500/20 rounded-xl text-red-500 font-black text-[9px] uppercase active:scale-95 transition-transform"
                    title="Borrar"
                  >
                    <Trash2 className="w-3.5 h-3.5 mr-1" /> Borrar
                  </button>

                  <button 
                    onClick={() => ad.estado === 'ACTIVO' ? marcarComoVendido(ad.id) : republicarAnuncio(ad.id)}
                    className={`flex-1 flex items-center justify-center py-2.5 rounded-xl font-black text-[9px] uppercase active:scale-95 transition-transform ${ad.estado === 'ACTIVO' ? 'bg-[#00B300] text-bg-dark' : 'bg-amber-500 text-bg-dark'}`}
                  >
                    {ad.estado === 'ACTIVO' ? (
                      <><CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Vendida</>
                    ) : (
                      <><RefreshCw className="w-3.5 h-3.5 mr-1" /> Republicar</>
                    )}
                  </button>
                </div>
              ) : (
                  <div className="flex gap-2 h-9 items-center justify-center">
                    <button 
                      onClick={() => handleCompartir(ad)}
                      className="w-10 h-full bg-slate-800/80 border border-white/10 rounded-2xl flex items-center justify-center text-slate-300 hover:text-white active:scale-95 transition-all shrink-0"
                      title="Compartir"
                    >
                      <Share2 className="w-4 h-4" />
                    </button>

                    <button 
                      onClick={() => {
                        const de = (ad.usuarios?.nombre || 'Vendedor').trim();
                        const mo = (ad.vehiculos?.apodo || 'Vehículo').trim();
                        const pr = (ad.precio || 0).toLocaleString();
                        const msg = `🚲 Hola *${de}*, vi tu vehículo *${mo}* publicado para la venta en el 💎 *Mercado VIP de Gravity Bike*, por un valor de: 💰 *$${pr}.*\n\n*Estoy interesado, ¿Aún lo tienes disponible?* 📲`;
                        window.open(`https://api.whatsapp.com/send?phone=57${ad.usuarios?.celular}&text=${encodeURIComponent(msg)}`, '_blank');
                      }}
                      className={`w-[58%] h-full flex items-center justify-center gap-2 rounded-2xl font-black text-[10px] uppercase tracking-widest transition-all ${ad.estado === 'VENDIDO' ? 'bg-slate-800 text-slate-500 cursor-not-allowed' : 'bg-[#00B300] text-black hover:brightness-110 shadow-[0_5px_15px_rgba(0,179,0,0.3)] active:scale-95'}`}
                      disabled={ad.estado === 'VENDIDO'}
                    >
                      <MessageCircle className="w-4 h-4" /> Negociar
                    </button>

                    <button 
                      onClick={() => reportarAnuncio(ad.id)}
                      className="w-10 h-full bg-white/5 border border-white/10 rounded-2xl flex items-center justify-center text-slate-500 hover:text-red-500 transition-colors shrink-0"
                      title="Reportar"
                    >
                      <Flag className="w-4 h-4" />
                    </button>
                  </div>
              )}
            </div>
          </motion.div>
        ))}

        {/* INDICADOR DE CARGA / FINAL */}
        {!viendoMisAnuncios && (
          <div className="py-10 text-center">
            {loadingMas ? (
               <div className="flex flex-col items-center gap-2 opacity-50">
                  <RefreshCw className="w-5 h-5 animate-spin text-primary-cyan" />
                  <span className="text-[8px] font-black uppercase tracking-widest">Cargando más ofertas...</span>
               </div>
            ) : !hayMas && anuncios.length > 0 ? (
               <div className="flex flex-col items-center gap-2 opacity-30">
                  <ShieldCheck className="w-6 h-6 text-primary-cyan" />
                  <span className="text-[8px] font-black uppercase tracking-widest">Estás al día. Estas son todas las joyas del mercado.</span>
               </div>
            ) : null}
          </div>
        )}
      </div>

      {/* MODAL PUBLICAR - REDISEÑO SEGÚN MOCKUP */}
      <AnimatePresence>
        {mostrarModalPublicar && (
          <motion.div 
            initial={{ y: '100%', opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: '100%', opacity: 0 }}
            className="fixed inset-0 z-[400] bg-black/95 backdrop-blur-xl flex flex-col pt-12 p-6 overflow-y-auto scrollbar-hide"
          >
            <div className="flex justify-between items-start mb-8 shrink-0">
               <div className="flex flex-col">
                  <h2 className="text-lg font-black text-white uppercase tracking-tighter leading-none mb-1">
                    {anuncioEnEdicion ? 'Editar' : 'Mercado VIP'}
                  </h2>
                  <span className="text-[8px] text-primary-cyan font-black uppercase tracking-[3px]">
                    {anuncioEnEdicion ? 'Actualizar Anuncio' : 'Nueva Publicación'}
                  </span>
               </div>
               <button 
                 onClick={() => { setMostrarModalPublicar(false); setAnuncioEnEdicion(null); setTituloAnuncio(''); setPrecio(''); setDescripcion(''); setUbicacion(''); setFotosExtra([]); }} 
                 className="w-8 h-8 bg-white/5 border border-white/10 rounded-full flex items-center justify-center text-slate-500 active:scale-90 transition-all"
               >
                 <X className="w-4 h-4" />
               </button>
            </div>

            <div className="space-y-3 pb-20">
              {/* SECCIÓN 1: TÍTULO DEL ANUNCIO */}
              <div className="bg-slate-800/50 backdrop-blur-xl border border-white/5 rounded-3xl p-3">
                <label className="text-[9px] font-black text-primary-cyan uppercase tracking-widest mb-2 flex items-center gap-2">
                  <ShieldCheck className="w-3.5 h-3.5" /> Título de la Bicicleta
                </label>
                <input 
                  type="text"
                  placeholder="Ej. Bicicleta Trek Marlin 5, Gw Lumen..."
                  value={tituloAnuncio}
                  onChange={e => setTituloAnuncio(e.target.value)}
                  className="w-full bg-slate-800/80 border border-white/10 rounded-2xl p-3 text-white font-bold text-sm outline-none focus:border-primary-cyan uppercase"
                />
              </div>

              {/* SECCIÓN 2: GALERÍA */}
              <div className="bg-slate-800/50 backdrop-blur-xl border border-white/5 rounded-3xl p-3">
                <div className="flex justify-between items-center mb-2">
                  <label className="text-[9px] font-black text-primary-cyan uppercase tracking-widest">Galería Comercial</label>
                  <span className="text-[9px] text-slate-600 font-bold">({fotosExtra.length}/3)</span>
                </div>
                
                <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
                  <input 
                    type="file" 
                    id="foto-upload" 
                    className="hidden" 
                    accept="image/*" 
                    onChange={handleSubirFoto} 
                  />
                  
                  <button 
                    onClick={() => document.getElementById('foto-upload')?.click()}
                    disabled={comprimiendo || fotosExtra.length >= 3}
                    className="w-20 h-20 bg-slate-800/80 border-2 border-dashed border-white/10 rounded-2xl flex flex-col items-center justify-center gap-1 shrink-0 active:scale-95 transition-transform disabled:opacity-50"
                  >
                    {comprimiendo ? (
                      <RefreshCw className="w-5 h-5 text-primary-cyan animate-spin" />
                    ) : (
                      <>
                        <Camera className="w-5 h-5 text-slate-500" />
                        <span className="text-[7px] font-black text-slate-600 uppercase">Añadir</span>
                      </>
                    )}
                  </button>

                  {fotosExtra.map((url, idx) => (
                    <div key={idx} className="relative w-20 h-20 shrink-0">
                      <img src={url} className="w-full h-full object-cover rounded-2xl border border-white/10" />
                      <button 
                        onClick={() => eliminarFoto(url)}
                        className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full p-1 shadow-lg"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {/* SECCIÓN 3: PRECIO Y UBICACIÓN */}
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-slate-800/50 backdrop-blur-xl border border-white/5 rounded-3xl p-3 flex flex-col gap-1">
                  <label className="text-[9px] font-black text-gold-cert uppercase tracking-widest">Precio (COP)</label>
                  <div className="flex items-center gap-1">
                    <span className="text-slate-600 font-black text-base">$</span>
                    <input 
                      type="text"
                      placeholder="0"
                      value={precio}
                      onChange={e => setPrecio(e.target.value.replace(/\D/g, '').replace(/\B(?=(\d{3})+(?!\d))/g, "."))}
                      className="bg-transparent text-white font-black text-base w-full outline-none"
                    />
                  </div>
                </div>

                <div className="bg-slate-800/50 backdrop-blur-xl border border-white/5 rounded-3xl p-3 flex flex-col gap-1">
                  <label className="text-[9px] font-black text-primary-cyan uppercase tracking-widest">Ciudad/Ubicación</label>
                  <input 
                    type="text"
                    placeholder="Ej: Cali, Valle"
                    value={ubicacion}
                    onChange={e => setUbicacion(e.target.value)}
                    className="bg-transparent text-white font-black text-[11px] w-full outline-none pt-1 placeholder:text-slate-600 uppercase"
                  />
                </div>
              </div>

              {/* SECCIÓN 4: DESCRIPCIÓN */}
              <div className="bg-slate-800/50 backdrop-blur-xl border border-white/5 rounded-3xl p-3">
                <label className="text-[9px] font-black text-slate-500 uppercase tracking-widest mb-2 block">Descripción del Estado</label>
                <textarea 
                  placeholder="Único dueño, peritaje al día..."
                  value={descripcion}
                  onChange={e => setDescripcion(e.target.value)}
                  rows={6}
                  className="w-full bg-transparent text-white text-sm font-medium outline-none placeholder:text-slate-700"
                />
              </div>

              {/* BOTÓN FINAL */}
              <button 
                onClick={publicarAnuncio}
                disabled={publicando || !tituloAnuncio || !precio || fotosExtra.length === 0}
                className={`w-full py-4 rounded-2xl flex items-center justify-center gap-3 font-black text-xs uppercase tracking-widest transition-all shadow-xl active:scale-95 ${(!tituloAnuncio || !precio || fotosExtra.length === 0) ? 'bg-slate-800 text-slate-600 opacity-50' : 'bg-blue-600 text-white hover:bg-blue-500 shadow-blue-900/40'}`}
              >
                {publicando ? (
                   <RefreshCw className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <ShoppingBag className="w-4 h-4" /> 
                    {anuncioEnEdicion ? 'Guardar Cambios' : 'Publicar en Mercado VIP'}
                  </>
                )}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>



      {/* DIALOGO CONFIRMACION LOCAL */}
      <AnimatePresence>
        {dialogoConfirmacion && (
          <motion.div 
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[600] bg-black/90 backdrop-blur-md flex items-center justify-center p-6"
          >
            <motion.div 
              initial={{ scale: 0.9 }} animate={{ scale: 1 }}
              className={`w-full max-w-xs bg-[#111] border ${dialogoConfirmacion.color === 'amber' ? 'border-amber-500' : dialogoConfirmacion.color === 'red' ? 'border-red-500' : 'border-emerald-500'} rounded-[40px] p-8 text-center relative overflow-hidden`}
            >
              <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 border ${dialogoConfirmacion.color === 'amber' ? 'bg-amber-500/10 border-amber-500/20 text-amber-500' : dialogoConfirmacion.color === 'red' ? 'bg-red-500/10 border-red-500/20 text-red-500' : 'bg-emerald-500/10 border-emerald-500/20 text-emerald-500'}`}>
                {dialogoConfirmacion.color === 'emerald' ? <CheckCircle2 className="w-8 h-8" /> : <AlertTriangle className="w-8 h-8" />}
              </div>
              
              <h3 className="text-white font-black text-lg mb-2 uppercase tracking-tight">{dialogoConfirmacion.titulo}</h3>
              <p className="text-slate-400 text-xs leading-relaxed mb-6">{dialogoConfirmacion.mensaje}</p>

              <div className="flex flex-col gap-3">
                {dialogoConfirmacion.accion && (
                  <button 
                    onClick={() => { dialogoConfirmacion.accion!(); setDialogoConfirmacion(null); }}
                    className="w-full bg-red-600 text-white py-3 rounded-2xl font-black text-[11px] uppercase tracking-widest"
                  >
                    Confirmar
                  </button>
                )}
                <button 
                  onClick={() => setDialogoConfirmacion(null)}
                  className="w-full bg-white text-bg-dark py-3 rounded-2xl font-black text-[11px] uppercase tracking-widest active:scale-95 transition-transform"
                >
                  Entendido
                </button>

                {dialogoConfirmacion.titulo === "ACCESO RESTRINGIDO" && (
                  <button 
                    onClick={() => {
                      setDialogoConfirmacion(null);
                      document.dispatchEvent(new CustomEvent('abrirMembresiaOro'));
                    }}
                    className="w-full bg-gradient-to-r from-amber-600 to-amber-400 text-black py-4 rounded-2xl font-black text-[12px] uppercase tracking-widest active:scale-95 transition-transform shadow-xl shadow-amber-600/30 flex items-center justify-center gap-2"
                  >
                    <Crown className="w-5 h-5" />
                    Hazte Miembro ORO
                  </button>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* VISOR DE FOTOS FULL SCREEN */}
      <AnimatePresence>
        {visorFotos && visorFotos.fotos.length > 0 && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-2xl flex flex-col items-center pt-8 overflow-y-auto"
          >
            {/* Cabecera / Contador */}
            <div className="w-full max-w-lg px-6 flex items-center justify-between mb-4">
              <span className="text-white/40 font-black text-[10px] uppercase tracking-[4px]">
                {visorFotos.index + 1} / {visorFotos.fotos.length}
              </span>
              <button 
                onClick={() => setVisorFotos(null)}
                className="w-10 h-10 bg-white/5 border border-white/10 rounded-full flex items-center justify-center text-white/50 active:scale-90 transition-all hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* FOTO PRINCIPAL Y FLECHAS */}
            <div className="relative w-full max-w-lg px-4 flex flex-col items-center">
              <motion.div 
                key={visorFotos.index}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="relative w-full aspect-[4/3] rounded-[40px] overflow-hidden shadow-3xl border border-white/10"
              >
                <img 
                  src={visorFotos.fotos[visorFotos.index]} 
                  alt="Vista Detalle" 
                  className="w-full h-full object-cover"
                />
              </motion.div>

              {/* Botones de Navegación Lateral (Sobre la foto pero sutiles) */}
              {visorFotos.fotos.length > 1 && (
                <>
                  <button 
                    onClick={() => setVisorFotos(prev => prev ? { ...prev, index: (prev.index - 1 + prev.fotos.length) % prev.fotos.length } : null)}
                    className="absolute left-6 top-[35%] w-10 h-10 bg-black/40 backdrop-blur-md rounded-full flex items-center justify-center text-white border border-white/10 active:scale-90 transition-all shadow-xl"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <button 
                    onClick={() => setVisorFotos(prev => prev ? { ...prev, index: (prev.index + 1) % prev.fotos.length } : null)}
                    className="absolute right-6 top-[35%] w-10 h-10 bg-black/40 backdrop-blur-md rounded-full flex items-center justify-center text-white border border-white/10 active:scale-90 transition-all shadow-xl"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </>
              )}

              {/* Tira de Miniaturas */}
              <div className="flex items-center justify-center gap-2 overflow-x-auto py-4 w-full scrollbar-hide">
                {visorFotos.fotos.map((img, idx) => (
                  <button 
                    key={idx}
                    onClick={() => setVisorFotos(prev => prev ? { ...prev, index: idx } : null)}
                    className={`w-9 h-9 rounded-xl shrink-0 overflow-hidden border-2 transition-all ${visorFotos.index === idx ? 'border-amber-500' : 'border-white/5 opacity-30'}`}
                  >
                    <img src={img} className="w-full h-full object-cover" alt="Thumb" />
                  </button>
                ))}
              </div>
            </div>

            {/* FICHA TÉCNICA (FUERA DE LA FOTO) */}
            <div className="w-full max-w-lg px-6 pb-20 mt-2">
              <div className="bg-white/5 border border-white/5 rounded-[30px] p-5 backdrop-blur-md">
                <div className="flex justify-between items-start mb-4">
                  <div className="flex flex-col gap-1">
                    <h4 className="text-sm font-black text-white uppercase leading-none tracking-tight">
                      {visorFotos.ad.vehiculos?.apodo || 'ARTÍCULO VIP'}
                    </h4>
                    <div className="flex flex-col gap-1.5 mt-1">
                      <span className="text-[9px] font-black text-white/40 flex items-center gap-1 uppercase">
                        <MapPin className="w-3 h-3" /> {visorFotos.ad.ciudad || 'Ubicación'}
                      </span>
                    </div>
                  </div>
                  
                  <div className="flex flex-col items-end">
                    <span className="text-lg font-black text-white leading-none">
                      <span className="text-xs mr-0.5">$</span>{visorFotos.ad.precio?.toLocaleString()}
                    </span>
                  </div>
                </div>

                <div className="bg-white/5 rounded-2xl p-4 mb-4">
                  <p className="text-xs text-slate-300 font-medium leading-relaxed">
                    {visorFotos.ad.descripcion || 'Sin descripción disponible.'}
                  </p>
                </div>

                {visorFotos.ad.estado === 'VENDIDO' ? (
                  <div className="w-full bg-slate-800/80 text-slate-500 py-3 rounded-2xl font-black text-[11px] uppercase border border-white/5 flex items-center justify-center gap-2">
                    <CheckCircle2 className="w-4 h-4" /> Vehículo Ya Vendido
                  </div>
                ) : (
                  <div className="flex flex-col gap-3">
                    <button 
                      onClick={() => {
                        const de = (visorFotos.ad.usuarios?.nombre || 'Vendedor').trim();
                        const mo = (visorFotos.ad.vehiculos?.apodo || 'Vehículo').trim();
                        const pr = (visorFotos.ad.precio || 0).toLocaleString();
                        const msg = `🚲 Hola *${de}*, vi tu vehículo *${mo}* publicado para la venta en el 💎 *Mercado VIP de Gravity Bike*, por un valor de: 💰 *$${pr}.*\n\n*Estoy interesado, ¿Aún lo tienes disponible?* 📲`;
                        window.open(`https://api.whatsapp.com/send?phone=57${visorFotos.ad.usuarios?.celular}&text=${encodeURIComponent(msg)}`, '_blank');
                      }}
                      className="w-full bg-[#00B300] text-black py-3 rounded-2xl font-black text-[11px] uppercase shadow-lg shadow-[#00B300]/20 active:scale-95 transition-all flex items-center justify-center gap-2"
                    >
                      <MessageCircle className="w-4 h-4" /> Chatear con Vendedor
                    </button>

                    <button 
                      onClick={() => handleCompartir(visorFotos.ad)}
                      className="w-full bg-white/5 border border-white/10 text-white py-3 rounded-2xl font-black text-[11px] uppercase active:scale-95 transition-all flex items-center justify-center gap-2"
                    >
                      <Share2 className="w-4 h-4" /> Compartir Vehículo
                    </button>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
